import { defineMessages } from 'react-intl';
import { reset } from 'redux-form';
import { all, takeEvery, call, put, delay, select } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { forceRedirectOutsideReact, reloadPage, openNewTab } from 'helpers/page-utils';
import { PASSWORD_UPDATE_FORM_NAME } from 'constants/logic';
import {
  logOut,
  updateLocale as updateLocaleEntity,
  updatePassword as updatePasswordEntity,
  brazeData,
} from 'store/saga/entities/bound';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { reportError } from 'helpers/error-logging';
import { selectActiveVenueId } from 'selectors/general';

export const messages = defineMessages({
  successUpdatePW: {
    id: 'settings.saga.success-update-pw',
    defaultMessage: 'You updated your password',
  },
  errorUpdatePW: {
    id: 'settings.saga.error-update-pw',
    defaultMessage: 'We were unable to update your password at this time',
  },
  successUpdateLocale: {
    id: 'settings.saga.success-update-locale',
    defaultMessage: 'Your language has been updated',
  },
  errorUpdateLocale: {
    id: 'settings.saga.error-update-locale',
    defaultMessage: 'We were unable to update your language at this time',
  },
});

export const UPDATE_LOCALE = 'venuePortal/settingsAccount/UPDATE_LOCALE';
export const updateLocale = locale => ({ type: UPDATE_LOCALE, locale });

export const UPDATE_PASSWORD = 'venuePortal/settingsAccount/UPDATE_PASSWORD';
export const updatePassword = newPassword => ({ type: UPDATE_PASSWORD, newPassword });

export const LOG_OUT_ACTION = 'venuePortal/settingsAccount/LOG_OUT_ACTION';
export const logOutAction = () => ({ type: LOG_OUT_ACTION });

export const REQUEST_NEW_TAB = 'venuePortal/settingsAccount/REQUEST_NEW_TAB';
export const requestNewTab = path => ({ type: REQUEST_NEW_TAB, path });

export const RELOAD_DELAY_LENGTH = 2000;

export const venueSelection = venueId => ({ selectedVenueId: venueId });

export function* doUpdateLocale(dependencies, action) {
  try {
    const { locale } = action;
    yield call(fetchEntity, updateLocaleEntity, [dependencies, locale]);
    yield put(createInfoBanner(messages.successUpdateLocale));
    yield delay(RELOAD_DELAY_LENGTH);
    yield call(reloadPage, true);
  } catch (error) {
    yield all([put(createErrorBanner(messages.errorUpdateLocale)), call(reportError, error)]);
  }
}

export function* doUpdatePassword(dependencies, action) {
  try {
    const { newPassword } = action;
    yield call(fetchEntity, updatePasswordEntity, [dependencies, newPassword]);
    yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.PASSWORD_CHANGED);
    yield all([put(reset(PASSWORD_UPDATE_FORM_NAME)), put(createInfoBanner(messages.successUpdatePW))]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.errorUpdatePW)), call(reportError, error)]);
  }
}

export function* doLogOutAction(dependencies) {
  yield all([call(fetchEntity, logOut, [dependencies]), call(forceRedirectOutsideReact, '/')]);
}

export function* doRequestNewTab(dependencies, action) {
  const { path } = action;
  yield call(openNewTab, path);
}

export function* handleProfileAccountSettingsEnter(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    if (!venueId) return;
    yield call(fetchEntity, brazeData, [client, venueId]);
  } catch (error) {
    yield call(reportError, error);
  }
}

export function* watchActions(dependencies) {
  yield takeEvery(UPDATE_LOCALE, doUpdateLocale, dependencies);
  yield takeEvery(UPDATE_PASSWORD, doUpdatePassword, dependencies);
  yield takeEvery(LOG_OUT_ACTION, doLogOutAction, dependencies);
  yield takeEvery(REQUEST_NEW_TAB, doRequestNewTab, dependencies);
}
